/**
 * The visitor's own screens (brief sections 6.4 and 7.5): Hand the tablet
 * and, later, the booth kiosk. Six languages; the texts are the
 * prototype's (docs/prototype.html, KL), written by Claude, to be reviewed
 * by native speakers later without blocking. The values sent stay the
 * portal's own (interest, role, WR-1 to WR-4, countries as English names):
 * only what the visitor reads changes.
 */
export const VISITOR_LANGUAGES = ['en', 'fr', 'de', 'nl', 'it', 'es'] as const;
export type VisitorLanguage = (typeof VISITOR_LANGUAGES)[number];

export interface VisitorTexts {
  /** The language's own name, on its button. */
  language: string;
  title: string;
  sub: string;
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  more: string;
  jobTitle: string;
  mobile: string;
  officePhone: string;
  linkedin: string;
  country: string;
  countryPick: string;
  interest: string;
  /** In the order of the interest values: active, browsing. */
  interests: [string, string];
  role: string;
  /** In the order of the role values: decides, influences, scoping, none. */
  roles: [string, string, string, string];
  applications: string;
  /** WR-1 to WR-4, without their codes. */
  markets: [string, string, string, string];
  pain: string;
  sendMe: string;
  /** The consent sentence: its version is privacy.consentVersion of config/portal.json. */
  consent: string;
  privacy: string;
  send: string;
  required: string;
  badEmail: string;
  invalid: string;
  consentNeeded: string;
  thanks: string;
  /** Followed by the seller's first name. */
  handBack: string;
}

export const VISITOR_TEXTS: Record<VisitorLanguage, VisitorTexts> = {
  en: {
    language: 'English',
    title: 'Tell us about you',
    sub: 'We will send you the information right after the show.',
    firstName: 'First name',
    lastName: 'Last name',
    company: 'Company',
    email: 'Email',
    more: 'More info (optional)',
    jobTitle: 'Job title',
    mobile: 'Mobile phone',
    officePhone: 'Office phone',
    linkedin: 'LinkedIn',
    country: 'Country',
    countryPick: 'Select a country',
    interest: 'Are you working on a sorting project?',
    interests: ['Yes, an active project', 'Just looking for now'],
    role: 'Your role in a purchase like this',
    roles: [
      'I decide',
      'I influence the decision',
      'I research it for someone else',
      'Not involved',
    ],
    applications: 'Which applications interest you?',
    markets: ['Bag sorting', 'Construction & demolition', 'Recyclables', 'Metals'],
    pain: 'What do you sort, and what is the most frustrating part today?',
    sendMe: 'Send me information after the show',
    consent:
      'I agree that Waste Robotics contacts me about its sorting solutions. I can unsubscribe at any time.',
    privacy: 'Privacy policy',
    send: 'Send',
    required: 'Please fill in the fields marked with *.',
    badEmail: 'Please check the email address.',
    invalid: 'Please check this field.',
    consentNeeded: 'Please tick the box to agree, so we can contact you.',
    thanks: 'Thank you!',
    handBack: 'Please hand the tablet back to',
  },
  fr: {
    language: 'Français',
    title: 'Parlez-nous de vous',
    sub: 'Nous vous enverrons l’information juste après le salon.',
    firstName: 'Prénom',
    lastName: 'Nom',
    company: 'Entreprise',
    email: 'Courriel',
    more: 'Plus d’informations (facultatif)',
    jobTitle: 'Poste',
    mobile: 'Mobile',
    officePhone: 'Téléphone au bureau',
    linkedin: 'LinkedIn',
    country: 'Pays',
    countryPick: 'Choisir un pays',
    interest: 'Travaillez-vous sur un projet de tri ?',
    interests: ['Oui, un projet actif', 'Je m’informe pour l’instant'],
    role: 'Votre rôle dans un achat de ce type',
    roles: [
      'Je décide',
      'J’influence la décision',
      'Je m’informe pour quelqu’un d’autre',
      'Pas impliqué',
    ],
    applications: 'Quelles applications vous intéressent ?',
    markets: ['Tri de sacs', 'Construction et démolition', 'Matières recyclables', 'Métaux'],
    pain: 'Que triez-vous, et qu’est-ce qui est le plus frustrant aujourd’hui ?',
    sendMe: 'Envoyez-moi de l’information après le salon',
    consent:
      'J’accepte que Waste Robotics me contacte au sujet de ses solutions de tri. Je peux me désabonner en tout temps.',
    privacy: 'Politique de confidentialité',
    send: 'Envoyer',
    required: 'Veuillez remplir les champs marqués d’un *.',
    badEmail: 'Veuillez vérifier l’adresse courriel.',
    invalid: 'Veuillez vérifier ce champ.',
    consentNeeded: 'Veuillez cocher la case pour que nous puissions vous contacter.',
    thanks: 'Merci !',
    handBack: 'Veuillez remettre la tablette à',
  },
  de: {
    language: 'Deutsch',
    title: 'Erzählen Sie uns von sich',
    sub: 'Wir senden Ihnen die Informationen direkt nach der Messe.',
    firstName: 'Vorname',
    lastName: 'Nachname',
    company: 'Unternehmen',
    email: 'E-Mail',
    more: 'Weitere Angaben (optional)',
    jobTitle: 'Position',
    mobile: 'Mobilnummer',
    officePhone: 'Telefon (Büro)',
    linkedin: 'LinkedIn',
    country: 'Land',
    countryPick: 'Land auswählen',
    interest: 'Arbeiten Sie an einem Sortierprojekt?',
    interests: ['Ja, ein aktives Projekt', 'Ich informiere mich nur'],
    role: 'Ihre Rolle bei einer solchen Investition',
    roles: [
      'Ich entscheide',
      'Ich beeinflusse die Entscheidung',
      'Ich recherchiere für jemand anderen',
      'Nicht beteiligt',
    ],
    applications: 'Welche Anwendungen interessieren Sie?',
    markets: ['Sacksortierung', 'Bau- und Abbruchabfälle', 'Wertstoffe', 'Metalle'],
    pain: 'Was sortieren Sie, und was ist heute das größte Ärgernis?',
    sendMe: 'Senden Sie mir nach der Messe Informationen',
    consent:
      'Ich bin einverstanden, dass Waste Robotics mich zu seinen Sortierlösungen kontaktiert. Ich kann mich jederzeit abmelden.',
    privacy: 'Datenschutzerklärung',
    send: 'Senden',
    required: 'Bitte füllen Sie die mit * markierten Felder aus.',
    badEmail: 'Bitte prüfen Sie die E-Mail-Adresse.',
    invalid: 'Bitte prüfen Sie dieses Feld.',
    consentNeeded: 'Bitte kreuzen Sie das Kästchen an, damit wir Sie kontaktieren können.',
    thanks: 'Vielen Dank!',
    handBack: 'Bitte geben Sie das Tablet zurück an',
  },
  nl: {
    language: 'Nederlands',
    title: 'Vertel ons over u',
    sub: 'We sturen u de informatie direct na de beurs.',
    firstName: 'Voornaam',
    lastName: 'Achternaam',
    company: 'Bedrijf',
    email: 'E-mail',
    more: 'Meer informatie (optioneel)',
    jobTitle: 'Functie',
    mobile: 'Mobiel',
    officePhone: 'Telefoon (kantoor)',
    linkedin: 'LinkedIn',
    country: 'Land',
    countryPick: 'Kies een land',
    interest: 'Werkt u aan een sorteerproject?',
    interests: ['Ja, een actief project', 'Ik oriënteer me alleen'],
    role: 'Uw rol bij een dergelijke aankoop',
    roles: [
      'Ik beslis',
      'Ik beïnvloed de beslissing',
      'Ik zoek het uit voor iemand anders',
      'Niet betrokken',
    ],
    applications: 'Welke toepassingen interesseren u?',
    markets: ['Zakkensortering', 'Bouw- en sloopafval', 'Recyclebare materialen', 'Metalen'],
    pain: 'Wat sorteert u, en wat is vandaag het meest frustrerend?',
    sendMe: 'Stuur mij na de beurs informatie',
    consent:
      'Ik ga ermee akkoord dat Waste Robotics contact met mij opneemt over haar sorteeroplossingen. Ik kan mij altijd afmelden.',
    privacy: 'Privacybeleid',
    send: 'Versturen',
    required: 'Vul de velden met een * in.',
    badEmail: 'Controleer het e-mailadres.',
    invalid: 'Controleer dit veld.',
    consentNeeded: 'Vink het vakje aan zodat we contact met u kunnen opnemen.',
    thanks: 'Dank u wel!',
    handBack: 'Geef de tablet terug aan',
  },
  it: {
    language: 'Italiano',
    title: 'Parlateci di voi',
    sub: 'Vi invieremo le informazioni subito dopo la fiera.',
    firstName: 'Nome',
    lastName: 'Cognome',
    company: 'Azienda',
    email: 'E-mail',
    more: 'Altre informazioni (facoltativo)',
    jobTitle: 'Ruolo',
    mobile: 'Cellulare',
    officePhone: 'Telefono ufficio',
    linkedin: 'LinkedIn',
    country: 'Paese',
    countryPick: 'Scegliete un paese',
    interest: 'State lavorando a un progetto di selezione?',
    interests: ['Sì, un progetto attivo', 'Mi sto solo informando'],
    role: 'Il vostro ruolo in un acquisto di questo tipo',
    roles: ['Decido io', 'Influenzo la decisione', 'Mi informo per qualcun altro', 'Non coinvolto'],
    applications: 'Quali applicazioni vi interessano?',
    markets: [
      'Selezione dei sacchi',
      'Costruzione e demolizione',
      'Materiali riciclabili',
      'Metalli',
    ],
    pain: 'Cosa selezionate, e qual è oggi la parte più frustrante?',
    sendMe: 'Inviatemi informazioni dopo la fiera',
    consent:
      'Accetto di essere contattato da Waste Robotics riguardo alle sue soluzioni di selezione. Posso annullare l’iscrizione in qualsiasi momento.',
    privacy: 'Informativa sulla privacy',
    send: 'Invia',
    required: 'Compilate i campi contrassegnati da *.',
    badEmail: 'Controllate l’indirizzo e-mail.',
    invalid: 'Controllate questo campo.',
    consentNeeded: 'Spuntate la casella per permetterci di contattarvi.',
    thanks: 'Grazie!',
    handBack: 'Restituisca il tablet a',
  },
  es: {
    language: 'Español',
    title: 'Cuéntenos sobre usted',
    sub: 'Le enviaremos la información justo después de la feria.',
    firstName: 'Nombre',
    lastName: 'Apellido',
    company: 'Empresa',
    email: 'Correo electrónico',
    more: 'Más información (opcional)',
    jobTitle: 'Cargo',
    mobile: 'Móvil',
    officePhone: 'Teléfono de oficina',
    linkedin: 'LinkedIn',
    country: 'País',
    countryPick: 'Elija un país',
    interest: '¿Está trabajando en un proyecto de clasificación?',
    interests: ['Sí, un proyecto activo', 'Solo me estoy informando'],
    role: 'Su papel en una compra de este tipo',
    roles: [
      'Yo decido',
      'Influyo en la decisión',
      'Lo investigo para otra persona',
      'No participo',
    ],
    applications: '¿Qué aplicaciones le interesan?',
    markets: ['Clasificación de bolsas', 'Construcción y demolición', 'Reciclables', 'Metales'],
    pain: '¿Qué clasifica, y qué es lo más frustrante hoy?',
    sendMe: 'Envíenme información después de la feria',
    consent:
      'Acepto que Waste Robotics me contacte sobre sus soluciones de clasificación. Puedo darme de baja en cualquier momento.',
    privacy: 'Política de privacidad',
    send: 'Enviar',
    required: 'Complete los campos marcados con *.',
    badEmail: 'Revise la dirección de correo electrónico.',
    invalid: 'Revise este campo.',
    consentNeeded: 'Marque la casilla para que podamos contactarle.',
    thanks: '¡Gracias!',
    handBack: 'Por favor devuelva la tableta a',
  },
};
